import React, { useState, useRef, useEffect } from "react";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import {
  Modal,
  Box,
  Button,
  CircularProgress,
  Typography,
  Alert,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const ImageCropperModal = ({
  open,
  onClose,
  aspectRatio = 1 / 1,
  maxSizeKB = 1024,
  uploadUrl = "/api/upload",
  headers = {},
  onSuccess,
  onError,
}) => {
  const cropperRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [containerHeight, setContainerHeight] = useState(400);

  const handleFileSelect = (e) => {
    setError(null);
    const file = e.target.files[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }

    if (file.size > maxSizeKB * 1024) {
      setError(`File size exceeds ${maxSizeKB}KB limit`);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      // Get image dimensions to adjust container height
      const img = new Image();
      img.onload = () => {
        const maxHeight = Math.min(window.innerHeight * 0.6, 600);
        const ratio = img.width / img.height;
        const calculatedHeight = Math.min(
          maxHeight,
          (window.innerWidth * 0.8) / ratio
        );
        setContainerHeight(Math.max(300, calculatedHeight));
      };
      img.src = reader.result;
      setImageSrc(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const getCroppedImageBlob = () => {
    return new Promise((resolve) => {
      if (cropperRef.current) {
        const cropper = cropperRef.current.cropper;
        cropper.getCroppedCanvas().toBlob((blob) => {
          resolve(blob);
        }, "image/jpeg");
      }
    });
  };

  const handleCrop = async () => {
    const blob = await getCroppedImageBlob();
    setCroppedImage(URL.createObjectURL(blob));
  };

  const handleUpload = async () => {
    try {
      setLoading(true);
      const blob = await getCroppedImageBlob();
      const formData = new FormData();
      formData.append("image", blob, "cropped-image.png");
      const response = await fetch(uploadUrl, {
        method: "POST",
        headers: headers,
        body: formData,
      });

      if (!response.ok) throw new Error("Upload failed");

      const data = await response.json();
      onSuccess?.(data);
      onClose();
    } catch (err) {
      console.log(err);
      setError(err.message);
      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setImageSrc(null);
    setCroppedImage(null);
    setError(null);
    setLoading(false);
    setContainerHeight(400);
  };

  useEffect(() => {
    if (!open) resetState();
  }, [open]);

  const getExampleDimensions = (ratio) => {
    const decimalRatio = ratio;
    let width, height;

    if (decimalRatio === 1) {
      width = height = "1000";
    } else if (decimalRatio > 1) {
      width = "1600";
      height = Math.round(1600 / decimalRatio);
    } else {
      height = "900";
      width = Math.round(900 * decimalRatio);
    }

    return `${width}×${height}`;
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: 800,
          maxHeight: "90vh",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#888",
            borderRadius: "4px",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6">Crop and Upload Image</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!imageSrc ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <input
              accept="image/*"
              style={{ display: "none" }}
              id="image-upload"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="image-upload">
              <Button
                variant="contained"
                component="span"
                startIcon={<CloudUploadIcon />}
              >
                Select Image
              </Button>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Max size: {maxSizeKB}KB
              </Typography>
            </label>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                height: containerHeight,
                width: "100%",
                overflow: "hidden",
                position: "relative",
                mb: 2,
              }}
            >
              <Cropper
                ref={cropperRef}
                src={imageSrc}
                aspectRatio={aspectRatio}
                viewMode={1}
                guides={true}
                style={{
                  height: "100%",
                  width: "100%",
                  maxWidth: "100%",
                }}
              />
            </Box>

            {croppedImage && (
              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Typography variant="subtitle2">Preview:</Typography>
                <img
                  src={croppedImage}
                  alt="Cropped preview"
                  style={{
                    maxWidth: "200px",
                    maxHeight: "200px",
                    marginTop: "10px",
                    objectFit: "contain",
                  }}
                />
              </Box>
            )}

            <Box
              sx={{
                mt: 3,
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="contained"
                onClick={handleCrop}
                disabled={!imageSrc}
              >
                Preview Crop
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleUpload}
                disabled={!croppedImage || loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Upload Image"
                )}
              </Button>
            </Box>

            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary">
                Aspect Ratio: {aspectRatio.toFixed(2)} (Example:{" "}
                {getExampleDimensions(aspectRatio)})
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default ImageCropperModal;
